import { showAlertCallout } from "@/shared/alerts";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import type { UpdateOrganizationLanguageRequestDto } from "@/spz-api/model";
import type { UpdateConnectionAutoMembershipRequestDto } from "@/spz-api/model/updateConnectionAutoMembershipRequestDto";
import {
  OrganizationsController_updateOrganizationLanguage,
  updateAutoMembershipStatus,
  useGetOrganizationDetailsSuspense,
} from "@/spz-api/organization/organization";
import { useGetMe } from "@/spz-api/user/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
type UpdateConnectionAutoMembershipMutationVariables = {
  id: string;
  data: UpdateConnectionAutoMembershipRequestDto;
  headers?: Record<string, string>;
};
type UpdateOrgLanguageMutationVariables = {
  data: UpdateOrganizationLanguageRequestDto;
  headers?: Record<string, string>;
};
export const selectOrganizationSettingsViewModel = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const organizationId = me?.organization.id ?? "";

  const {
    data: organization,
    queryKey: organizationQueryKey,
    isPending: isOrganizationPending,
  } = useGetOrganizationDetailsSuspense({
    connectionTypes: true,
  });

  const updateAutoMembershipMutation = useMutation<
    null,
    unknown,
    UpdateConnectionAutoMembershipMutationVariables
  >({
    mutationFn: async (variables) => {
      const { id, data, headers } = variables;
      return updateAutoMembershipStatus(id, data, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKey });
    },
    onError: (error) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showAlertCallout({
          title: message,
          type: "critical",
        });
      }
    },
  });

  const ssoConnection = useMemo(() => {
    if (!organization?.connections?.length) return null;
    return organization.connections.find(
      (connection) => connection.isSSO === true,
    );
  }, [organization?.connections]);

  const isAutoMembershipEnabled =
    ssoConnection?.isAutoMembershipEnabled ?? false;

  const handleToggleChange = useCallback(
    (checked: boolean) => {
      const connectionId = ssoConnection?.id;
      if (!organizationId || !connectionId) return;
      updateAutoMembershipMutation.mutate({
        id: connectionId,
        data: { isAutoMembershipEnabled: checked },
        headers: organization.updatedAt
          ? { "if-match": organization.updatedAt }
          : undefined,
      });
    },
    [organizationId, ssoConnection, updateAutoMembershipMutation, organization],
  );

  const handleSsoRequest = useCallback(() => {
    if (!organization?.name) return;
    const subject = encodeURIComponent(
      `SSO Configuration Request - ${organization.name}`,
    );
    const body = encodeURIComponent(
      `Hi Supervizor team,

        I would like to enable SSO for my organization.

        Organization details:
        - Name: ${organization.name}

        Looking forward to hearing from you.

        Best regards,`,
    );
    window.location.href = `mailto:support@supervizor.com?subject=${subject}&body=${body}`;
  }, [organization?.name]);

  const updateOrganizationLanguageMutation = useMutation<
    null,
    unknown,
    UpdateOrgLanguageMutationVariables
  >({
    mutationFn: async (variables) => {
      const { data, headers } = variables;
      return OrganizationsController_updateOrganizationLanguage(data, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      showAlertCallout({
        title: t("organization_language_update_success"),
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: organizationQueryKey });
    },
    onError: (error) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showAlertCallout({
          title: message,
          type: "critical",
        });
      }
    },
  });

  const handleLanguageChange = (newLanguage: string) => {
    updateOrganizationLanguageMutation.mutate({
      data: { language: newLanguage },
      headers: organization.updatedAt
        ? { "if-match": organization.updatedAt }
        : undefined,
    });
  };

  const form = useForm({
    defaultValues: {
      language: organization?.language ?? "",
    },
  });

  useEffect(() => {
    if (organization?.language) {
      form.setValue("language", organization.language);
    }
  }, [organization?.language, form]);

  return {
    organization,
    handleLanguageChange,
    isOrganizationPending,
    ssoConnection,
    isAutoMembershipEnabled,
    handleToggleChange,
    handleSsoRequest,
    form,
  };
};
